import { APIEndpoints, FundDeposit, Organization, TeamMemberInvite } from '@/@account/types';
import { ERROR_MESSAGES } from '@/@cms/types';
import { tracking } from '@/lib/features/tracking';
import { portalApi, studioApi } from '@/lib/http/axios';

import { AxiosInstance } from 'axios';

// Configuration constants
const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Service class for interacting with the Studio Accounts API
 */
export class AccountsService {
    private readonly axiosInstance: AxiosInstance;
    private cache: Map<string, { data: any; timestamp: number }>;

    /**
     * Creates a new instance of the AccountsService
     */
    constructor() {
        this.axiosInstance = portalApi;

        // Initialize cache
        this.cache = new Map();
    }

    async getUserProfile() {
        try {
            const cacheKey = 'user_profile';
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) {
                return cachedData;
            }
            const { data: response } = await this.axiosInstance.get(APIEndpoints.USER_PROFILE);
            // Ensure we have data
            if (!response) {
                tracking.errorWithSource(
                    new Error('No user profile data received from the server'),
                    'studio.AccountsService.getUserProfile',
                );
                throw new Error('No user profile data received from the server');
            }
            // Cache the response data
            this.setCachedData(cacheKey, response);
            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get user profile: ${errorMessage}`),
                'studio.AccountsService.getUserProfile',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get user profile: ${errorMessage}`),
            );
        }
    }

    async updateUserProfile(params: Record<string, string>) {
        try {
            const { data: response } = await this.axiosInstance.put(
                APIEndpoints.USER_PROFILE,
                params,
            );
            // Ensure we have data
            if (!response) {
                tracking.errorWithSource(
                    new Error('No user profile data received from the server'),
                    'studio.AccountsService.updateUserProfile',
                );
                throw new Error('No user profile data received from the server');
            }
            // Invalidate cache for user profile
            this.invalidateCache('user_profile');
            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update user profile: ${errorMessage}`),
                'studio.AccountsService.updateUserProfile',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to update user profile: ${errorMessage}`),
            );
        }
    }

    async inviteTeamMembers(members: TeamMemberInvite[], orgId?: string) {
        try {
            const invitedEmails: string[] = [];
            for (const member of members) {
                // Build payload for each member
                const payload: TeamMemberInvite = {
                    email: member.email,
                    firstName: member.firstName,
                    lastName: member.lastName,
                    role: member.role,
                };
                if (orgId) payload.orgId = orgId;

                const { data: response } = await this.axiosInstance.post(
                    APIEndpoints.INVITE_TEAM_MEMBERS,
                    payload,
                );

                invitedEmails.push(response.data?.email || member.email);
            }

            // Invalidate cache for team members - both specific org and all
            if (orgId) this.invalidateCache(`team_members_${orgId}`);
            this.invalidateCache(`team_members_all`);
            return `Users Invited Successfully: ${invitedEmails.join(', ')}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to invite team members: ${errorMessage}`),
                'studio.AccountsService.inviteTeamMembers',
            );
            throw new Error(`Failed to invite team members: ${errorMessage}`);
        }
    }

    async getTeamMembers(params: { orgId?: string }) {
        try {
            const cacheKey = `team_members_${params.orgId || 'all'}`;
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) {
                return cachedData;
            }

            const { data: response } = await this.axiosInstance.get(APIEndpoints.TEAM_MEMBERS, {
                params,
            });

            // Ensure we have data
            if (response.hasError) {
                tracking.errorWithSource(
                    new Error('No users data received from the server'),
                    'studio.AccountsService.getUsers',
                );
                throw new Error('No users data received from the server');
            }

            // Cache the response data
            this.setCachedData(cacheKey, response.data);
            return response.data;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get users: ${errorMessage}`),
                'studio.AccountsService.getUsers',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(`Failed to get users: ${errorMessage}`));
        }
    }

    async updateTeamMemberRole(userId: number, params: { role: string; orgId?: string }) {
        try {
            const { role, orgId } = params;
            const { data: response } = await this.axiosInstance.put(
                `${APIEndpoints.TEAM_MEMBERS}/${userId}/role`,
                { role },
                { params: orgId ? { orgId } : undefined },
            );

            // Ensure we have data
            if (response.hasError || !response) {
                tracking.errorWithSource(
                    new Error('Failed to update team member role'),
                    'studio.AccountsService.updateTeamMemberRole',
                );
                throw new Error('Failed to update team member role');
            }

            // Invalidate cache for team members
            this.invalidateCache(`team_members`);
            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update team member role: ${errorMessage}`),
                'studio.AccountsService.updateTeamMemberRole',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to update team member role: ${errorMessage}`),
            );
        }
    }

    async removeTeamMember(params: { email: string; orgId?: string }) {
        try {
            const { data: response } = await this.axiosInstance.delete(
                APIEndpoints.USERS_BY_EMAIL,
                { params },
            );

            // Ensure we have data
            if (response.hasError || !response) {
                tracking.errorWithSource(
                    new Error('Failed to remove team member'),
                    'studio.AccountsService.removeTeamMember',
                );
                throw new Error('Failed to remove team member');
            }

            // Invalidate cache for team members
            this.invalidateCache(`team_members`);
            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to remove team member: ${errorMessage}`),
                'studio.AccountsService.removeTeamMember',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to remove team member: ${errorMessage}`),
            );
        }
    }

    async getOrganization(sfAccountId?: string) {
        try {
            const { data: response } = await this.axiosInstance.get(APIEndpoints.ORGANIZATION, {
                params: sfAccountId ? { orgId: sfAccountId } : undefined,
            });
            return response?.data ?? response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get organization: ${errorMessage}`),
                'studio.AccountsService.getOrganization',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get organization: ${errorMessage}`),
            );
        }
    }

    async searchClients(params: { searchTerm: string; searchField: string }) {
        try {
            const cacheKey = `search_clients_${params.searchTerm}`;
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) {
                return cachedData;
            }

            const { data: response } = await this.axiosInstance.get(APIEndpoints.ORG_LIST, {
                params: {
                    ...params,
                    limit: 20,
                    offset: 0,
                    sortBy: 'orgName',
                    sortOrder: 'ASC',
                },
            });

            let clients: { id: string; name: string; orgId: number }[] = [];

            if (response?.data?.results) {
                clients = response.data.results
                    .filter((item: Organization) => item.orgId && item.sfAccountId)
                    .map((item: Organization) => ({
                        id: item.sfAccountId,
                        name: item.orgName,
                        orgId: item.orgId,
                    }));
            }

            // Cache the response data
            this.setCachedData(cacheKey, clients);
            return clients;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to search clients: ${errorMessage}`),
                'studio.AccountsService.searchClients',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(`Failed to search clients: ${errorMessage}`));
        }
    }

    async getFundDeposits(params: Record<string, number | string | undefined>) {
        try {
            const cacheKey = `deposits_${JSON.stringify(params)}`;
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) {
                return cachedData;
            }

            const { data: response } = await portalApi.get(APIEndpoints.FUND_DEPOSITS, {
                params,
            });

            // Ensure we have data - response.data is the array from the new API
            if (response.hasError || !response.data) {
                tracking.errorWithSource(
                    new Error('No deposits data received from the server'),
                    'studio.AccountsService.getDeposits',
                );
                throw new Error('No deposits data received from the server');
            }

            // Cache the response data
            this.setCachedData(cacheKey, response.data);
            return response.data;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get deposits: ${errorMessage}`),
                'studio.AccountsService.getDeposits',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(`Failed to get deposits: ${errorMessage}`));
        }
    }

    async getFundsBalance(userId: string, forOrgId?: number) {
        try {
            const cacheKey = `funds_balance_${userId}_${forOrgId ?? 'home'}`;
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) {
                return cachedData;
            }

            const { data: response } = await portalApi.get(APIEndpoints.FUNDS_BALANCE, {
                params: forOrgId != null ? { forOrgId } : undefined,
            });

            // Ensure we have data - response.data is the balance string from the new API
            if (response.hasError || response.data === undefined) {
                tracking.errorWithSource(
                    new Error('No funds balance data received from the server'),
                    'studio.AccountsService.getFundsBalance',
                );
                throw new Error('No funds balance data received from the server');
            }

            // Cache the response data
            this.setCachedData(cacheKey, response.data);
            return response.data || '0';
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get funds balance: ${errorMessage}`),
                'studio.AccountsService.getFundsBalance',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get funds balance: ${errorMessage}`),
            );
        }
    }

    async getUserBuilderDiscount(userId: string) {
        try {
            const cacheKey = `user_builder_discount_${userId}`;
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) {
                return cachedData;
            }

            const { data: response } = await portalApi.get(`${APIEndpoints.USER_BUILDER_DISCOUNT}`);

            // Ensure we have data
            if (response.hasError || !response) {
                tracking.errorWithSource(
                    new Error('Failed to get user builder discount'),
                    'studio.AccountsService.getUserBuilderDiscount',
                );
                throw new Error('Failed to get user builder discount');
            }

            // Cache the response data
            this.setCachedData(cacheKey, response);
            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get user builder discount: ${errorMessage}`),
                'studio.AccountsService.getUserBuilderDiscount',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get user builder discount: ${errorMessage}`),
            );
        }
    }

    async addFundDeposit(params: { paymentMethod: string; amount: number }) {
        try {
            const { data: response } = await studioApi.post(APIEndpoints.ADD_FUNDS, params);

            // Ensure we have data
            if (response.hasError || !response) {
                tracking.errorWithSource(
                    new Error('Failed to add funds deposit'),
                    'studio.AccountsService.addFundDeposit',
                );
                throw new Error('Failed to add funds deposit');
            }

            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to add funds deposit: ${errorMessage}`),
                'studio.AccountsService.addFundDeposit',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to add funds deposit: ${errorMessage}`),
            );
        }
    }

    async getTransactionInvoice(params: FundDeposit) {
        try {
            const cacheKey = `transaction_invoice_${params.externalId}`;
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) {
                return cachedData;
            }

            const { data: response } = await portalApi.post(
                `${APIEndpoints.TRANSACTION_INVOICE}/${params.externalId}`,
                params,
            );

            // Ensure we have data - response.data contains the invoice data from new API
            if (response.hasError || !response.data?.data?.invoice) {
                tracking.errorWithSource(
                    new Error('Failed to get transaction invoice'),
                    'studio.AccountsService.getTransactionInvoice',
                );
                throw new Error('Failed to get transaction invoice');
            }

            // Cache the response data
            this.setCachedData(cacheKey, response.data.data.invoice);
            return response.data.data.invoice;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get transaction invoice: ${errorMessage}`),
                'studio.AccountsService.getTransactionInvoice',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get transaction invoice: ${errorMessage}`),
            );
        }
    }

    /**
     * Gets data from cache if it exists and is not expired
     *
     * @param key - Cache key
     * @returns Cached data or undefined if not in cache or expired
     */
    private getCachedData(key: string): any | undefined {
        const cached = this.cache.get(key);

        if (!cached) {
            return undefined;
        }

        const now = Date.now();

        // Check if cache is expired (5 minutes)
        if (now - cached.timestamp > DEFAULT_CACHE_TIME) {
            this.cache.delete(key);
            return undefined;
        }

        return cached.data;
    }

    /**
     * Sets data in cache
     *
     * @param key - Cache key
     * @param data - Data to cache
     */
    private setCachedData(key: string, data: any): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
        });
    }

    /**
     * Clears the entire cache
     */
    clearCache(): void {
        this.cache.clear();
    }

    /**
     * Invalidates a specific cache key
     *
     * @param key - Cache key to invalidate
     */
    invalidateCache(key: string): void {
        this.cache.delete(key);
    }
}

// Export a singleton instance for use throughout the application
export const accountsService = new AccountsService();

// Export default for testing and mocking
export default AccountsService;
